import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import "./styles.css";

import { Textarea } from "@/components/ui/textarea";
import { config as gameConfig, constants } from "../../../constants";
import { config } from "../../../config";
import { Character } from "../../../types";
import { useAppSelector } from "../../../redux/store";
import GamePage from "../game-page/game-page";
import PrevSubmission from "../../common/prev-submission/prev-submission";

import { GameContext } from "../../../contexts";
import StoryFooter from "./story-footer";
import { selectIsSolo, selectAccessibility2 } from "../../../redux/selectors";
import { SafeJoin } from "../../../util";
import { useTranslation } from "react-i18next";
import useInterval from "../../common/use-interval/use-interval";
import { StoryState } from "./story";
import { numberString } from "../character-submit/useCharacterSubmit";

const submitInterval = 2500;

type Props = {
  storyState?: number;
  deadline: number;
};

function title(characters?: Character[]) {
  if (characters) {
    return (
      <span className={"max-w-full"}>
        {SafeJoin(
          characters
            .filter((c) => c.selected)
            .map<React.ReactNode>((c) => (
              <span
                key={"story-character-" + c.id}
                className={"text-emphasis max-w-full break-words inline-block"}
              >
                {c.name}
              </span>
            )),
          " & "
        )}
      </span>
    );
  } else {
    return <span>oops</span>;
  }
}

const StoryTime = ({ storyState, deadline }: Props) => {
  const { submit } = useContext(GameContext);
  const { t } = useTranslation(["game", "common"]);

  const playerState = useAppSelector((state) => state.player.player);
  const gameState = useAppSelector((state) => state.game.game);
  const accessibility = useAppSelector(selectAccessibility2);

  const gameSettings = gameState?.settings;

  const isSolo = useAppSelector(selectIsSolo);

  const story = playerState?.current_story;
  const submission = playerState?.current_submission;

  const start = gameState?.submission?.reading_start || Number.MAX_SAFE_INTEGER;
  const submissionEnd = playerState?.submission?.submission_end;
  const end = submissionEnd && submissionEnd > 0 ? submissionEnd : Number.MAX_SAFE_INTEGER;
  const duration = end - start;

  const numSubmissions = gameSettings?.num_switches ?? 3;

  const totalSeconds = Math.ceil(duration / 1000);
  const paddedMinutes = Math.floor(totalSeconds / 60)
    .toString()
    .padStart(2, "0");
  const paddedSeconds = (Math.floor(totalSeconds) % 60)
    .toString()
    .padStart(2, "0");

  // Local state for content and done status
  const [content, setContent] = useState<string>(submission?.content ?? "");
  const [done, setDone] = useState(submission?.meta.done ?? false);

  // Refs to access current content/done in effects without adding to deps
  const contentRef = useRef(content);
  const doneRef = useRef(done);
  contentRef.current = content;
  doneRef.current = done;

  // Track what we've last saved to avoid redundant saves
  const lastSavedRef = useRef({
    content: submission?.content ?? "",
    done: submission?.meta.done ?? false,
  });

  // Track previous story state for detecting transitions
  const prevStoryStateRef = useRef(storyState);

  // Flagged submissions ref
  const flagged = useRef(new Set<number>());

  // Callback signature matches onToggle prop: (submissionId, checked)
  const handleFlag = useCallback((id: number, checked: boolean) => {
    if (checked) {
      flagged.current.add(id);
    } else {
      flagged.current.delete(id);
    }
  }, []);

  // Single consolidated save function - takes content/done as params for stable reference
  const save = useCallback(
    (contentToSave: string, doneToSave: boolean, noLoading?: boolean) => {
      if (!submission?.id) return;

      const hasContentChange = contentToSave !== lastSavedRef.current.content;
      const hasDoneChange = doneToSave !== lastSavedRef.current.done;

      if (!hasContentChange && !hasDoneChange) return;

      // Update last saved ref before sending
      lastSavedRef.current = { content: contentToSave, done: doneToSave };

      const playerAction = {
        body: {
          submission: {
            id: submission.id,
            content: contentToSave,
          },
          flagged_submissions: Array.from(flagged.current),
          done_submission: doneToSave,
        },
        code: "story/submit",
      };
      submit(playerAction, noLoading);
    },
    [submission?.id, submit]
  );

  // Handle done button click
  function handleDone() {
    const newDone = !done;
    setDone(newDone);
    save(content, newDone);
  }

  // Handle text changes
  function handleChange(e: React.ChangeEvent<HTMLTextAreaElement>) {
    setContent(e.target.value);
    setDone(false);
  }

  // Effect 1: Sync local state when we get a NEW submission (id changes)
  useEffect(() => {
    setContent(submission?.content ?? "");
    setDone(submission?.meta.done ?? false);
    lastSavedRef.current = {
      content: submission?.content ?? "",
      done: submission?.meta.done ?? false,
    };
  }, [submission?.id]);

  // Effect 2: Trigger save when transitioning TO SAVING state
  useEffect(() => {
    const wasNotSaving = prevStoryStateRef.current !== StoryState.SAVING;
    const isNowSaving = storyState === StoryState.SAVING;

    if (wasNotSaving && isNowSaving && submission?.id) {
      save(contentRef.current, doneRef.current);
    }

    prevStoryStateRef.current = storyState;
  }, [storyState, submission?.id, save]);

  // Auto-save interval during writing phase
  useInterval(() => {
    if (storyState === StoryState.WRITING && submission?.id) {
      save(content, done, true);
    }
  }, submitInterval);

  function checkPaste(e: React.ClipboardEvent<HTMLTextAreaElement>) {
    if (!config.longPaste && e.clipboardData.getData("text").length > 200) {
      e.preventDefault();
    }
  }

  function label() {
    return submission?.index === 0
      ? t("storyTime.body.startingStory")
      : submission?.index === numSubmissions - 1
        ? t("storyTime.body.endStory")
        : t("storyTime.body.middleStory");
  }

  function partMap(index: number, numSwitches: number) {
    if (index === 0) {
      return t("label.beginning", { ns: "common" });
    } else if (index === numSwitches - 1) {
      return t("label.end", { ns: "common" });
    } else {
      return `${t("label.part", { ns: "common" })} ${numberString(index + 1)}`;
    }
  }

  function rating() {
    if (story?.settings.mature) {
      return t("label.eighteenPlus", { ns: "common" });
    } else {
      return t("label.allAges", { ns: "common" });
    }
  }

  function titles() {
    const lines = [title(story?.characters)];

    const part = partMap(
      submission?.index as number,
      gameSettings?.num_switches ?? 0
    );

    if (!isSolo) {
      let line = t("storyTime.headers.default", {
        index: (story?.meta.index as number) + 1,
        part: part,
        rating: rating(),
      });
      lines.push(<>{line}</>);
    } else {
      lines.push(<>{part}</>);
    }
    return lines;
  }

  // Memoize submissions list to avoid recomputing on every render
  const submissionsListContent = useMemo(() => {
    const filtered = story?.submissions?.filter(
      (v) => v.index < (submission?.index ?? 1000)
    );
    return filtered && filtered.length > 0
      ? filtered.map<React.ReactNode>((value) => (
        <PrevSubmission
          key={value.id}
          submission={value}
          variant="flag"
          onToggle={handleFlag}
        />
      ))
      : null;
  }, [story?.submissions, submission?.index, handleFlag]);

  function subHeaders() {
    const lines = [
      t("storyTime.subheaders.default", {
        prefix: label(),
        minutes: paddedMinutes,
        seconds: paddedSeconds,
      }),
    ];
    if (
      gameSettings?.solo_writing ||
      gameSettings?.forced_prompts
    ) {
      lines.push(
        t("storyTime.subheaders.prompt", {
          prompt: playerState?.current_submission?.meta.prompt,
        })
      );
    }

    return lines;
  }

  const hasPrevSubmissions = submissionsListContent !== null;

  return (
    <GamePage
      titlesJsx={titles()}
      subHeaders={subHeaders()}
      footer={
        <StoryFooter
          onPlayerAction={submit}
          onDone={handleDone}
          done={done}
          deadline={deadline}
        />
      }
    >
      <div
        className={`flex-1 w-full flex flex-col ${hasPrevSubmissions && "divide-gray-drk divide-y divide-dashed divide-x-0"}`}
      >
        <div
          className={
            "relative m-1 whitespace-pre-wrap normal-case text-lg max-w-full divide-y-2 divide-gray-drk divide-dashed space-y-2"
          }
          style={constants.accessibleStyle(accessibility)}
        >
          {submissionsListContent}
        </div>
        <div
          className={`text-base flex flex-col justify-center border border-black mb-2 ${hasPrevSubmissions && "mt-2"}`}
        >
          <Textarea
            className="resize-none"
            onPaste={checkPaste}
            value={content}
            maxLength={gameConfig.maxSubmissionSize}
            disabled={storyState !== StoryState.WRITING}
            style={constants.accessibleStyle(accessibility)}
            onChange={handleChange}
          />
        </div>
      </div>
    </GamePage>
  );
};

export default StoryTime;
